import { TrendingUp, TrendingDown, Plus, Check, GitCompareArrows, Shield, Star, AlertTriangle, Radio, Swords, Handshake, Sparkles } from "lucide-react";
import { useSlip } from "../context/SlipContext";
import { useWatch } from "../context/WatchContext";
import { fmtOdds } from "../lib/api";
import { PropLeanBar, getValueSpot, getConsensus } from "./MarketLean";
import { TeamLogo } from "./TeamLogo";

const HitBar = ({ rate }) => {
  const color = rate >= 60 ? "#10B981" : rate >= 45 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-[#233248] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${rate}%`, background: color }} />
      </div>
      <span className="font-data text-xs font-semibold" style={{ color }}>
        {rate}%
      </span>
    </div>
  );
};

const LiveTracker = ({ live, metric, propId }) => {
  const STATUS = {
    cleared: { color: "#10B981", label: "Cleared" },
    on: { color: "#00E5FF", label: "On pace" },
    off: { color: "#EF4444", label: "Off pace" },
  };
  const s = STATUS[live.status] || STATUS.off;
  const pct = Math.max(0, Math.min(100, live.line ? (live.current / live.line) * 100 : 0));
  return (
    <div
      data-testid={`live-tracker-${propId}`}
      className="mt-3 rounded-lg border border-[#233248] bg-[#0B0F17] p-2.5"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1 text-[10px] font-data font-bold uppercase tracking-wide text-red-400">
          <Radio className="h-3 w-3 animate-pulse" /> Live {metric}
        </span>
        <span
          data-testid={`live-tracker-status-${propId}`}
          className="text-[10px] font-data font-bold px-1.5 py-0.5 rounded"
          style={{ color: s.color, background: `${s.color}1a` }}
        >
          {s.label}
        </span>
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-data">
          <b className="text-white text-lg tabular-nums">{live.current}</b>
          <span className="text-slate-500 text-xs"> / {live.line}</span>
        </span>
        <span className="font-data text-[11px] text-slate-500">proj {live.projected}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#233248] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: s.color }} />
      </div>
    </div>
  );
};

export const PropCard = ({ prop, onCompare, compareActive, onOpenPlayer, injury, live, teamColor }) => {
  const { addLeg, hasLeg } = useSlip();
  const { isWatched, toggleWatch } = useWatch();
  const over = prop.lean === "OVER";
  const defWeak = prop.opp_def_rank_vs_pos > prop.opp_total_teams * 0.6;
  const watched = isWatched(prop.id);
  const tc = teamColor || "#233248";
  const valueSpot = getValueSpot(prop);
  const consensus = !valueSpot && getConsensus(prop);

  return (
    <div
      data-testid={`prop-card-${prop.player_name.split(" ").pop().toLowerCase()}-${prop.metric.split(" ")[0].toLowerCase()}`}
      className={`rounded-2xl border p-4 transition-all ${
        compareActive
          ? "border-[#00E5FF] cyan-glow"
          : valueSpot
          ? "border-[#A855F7]/50"
          : consensus
          ? "border-[#10B981]/50"
          : "border-[#233248] hover:border-[#00E5FF]/40"
      }`}
      style={{
        background: `linear-gradient(135deg, ${tc}26 0%, #141C2B 55%)`,
        boxShadow: `inset 4px 0 0 ${tc}`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          data-testid={`open-player-${prop.player_id}`}
          onClick={() => onOpenPlayer(prop.player_id)}
          className="text-left min-w-0"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <TeamLogo sport={prop.sport} abbr={prop.team_abbr} color={tc} size="sm" />
            <span className="text-white font-semibold text-sm truncate hover:text-[#00E5FF] transition-colors">
              {prop.player_name}
            </span>
          </div>
          <div className="font-data text-[11px] text-slate-500">
            {prop.position}
            {prop.bookmaker && <span className="text-[#00E5FF]"> · {prop.bookmaker}</span>}
          </div>
          {injury?.questionable && (
            <span
              data-testid={`injury-badge-${prop.id}`}
              className="mt-1 inline-flex items-center gap-1 rounded-md bg-[#F59E0B]/15 text-[#F59E0B] text-[10px] font-bold px-1.5 py-0.5 font-data"
            >
              <AlertTriangle className="h-3 w-3" /> {injury.status}
            </span>
          )}
        </button>
        <span
          className={`shrink-0 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold font-data ${
            over ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"
          }`}
        >
          {over ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {prop.lean}
        </span>
        <button
          data-testid={`watch-prop-${prop.id}`}
          onClick={() => toggleWatch(prop)}
          title={watched ? "Tracking line movement" : "Track line movement"}
          className={`shrink-0 rounded-md p-1 transition-colors ${
            watched ? "text-[#F59E0B]" : "text-slate-500 hover:text-[#F59E0B]"
          }`}
        >
          <Star className="h-4 w-4" fill={watched ? "#F59E0B" : "none"} />
        </button>
      </div>

      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-xs text-slate-400">{prop.metric}</span>
        <span className="font-data text-white">
          <span className="text-[10px] text-slate-500 mr-1">line</span>
          <b className="text-lg">{prop.line}</b>
        </span>
      </div>

      <div className="mt-2.5">
        {prop.has_real_log ? (
          <>
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
              <span>L10 hit rate · real</span>
              {defWeak && (
                <span className="flex items-center gap-0.5 text-[#10B981]">
                  <Shield className="h-3 w-3" /> soft matchup
                </span>
              )}
            </div>
            <HitBar rate={prop.hit_rate} />
          </>
        ) : (
          <div className="text-[11px] text-slate-500">No game log available yet</div>
        )}
      </div>

      {live && <LiveTracker live={live} metric={prop.metric} propId={prop.id} />}

      {valueSpot && (
        <div
          data-testid={`value-spot-${prop.id}`}
          className="mt-3 flex items-center gap-2 rounded-lg border border-[#A855F7]/40 bg-[#A855F7]/10 px-2.5 py-2"
        >
          <Swords className="h-4 w-4 shrink-0 text-[#C084FC]" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-data font-bold uppercase tracking-wide text-[#C084FC]">
                Value Spot · Sharp vs Public
              </span>
            </div>
            <p className="text-[11px] text-slate-300 leading-tight">
              AI likes <b className="text-white">{valueSpot.aiSide}</b> but the market leans{" "}
              <b className="text-white">{valueSpot.marketSide} {valueSpot.marketPct}%</b>
            </p>
          </div>
        </div>
      )}

      {consensus && (
        <div
          data-testid={`consensus-pick-${prop.id}`}
          className="mt-3 flex items-center gap-2 rounded-lg border border-[#10B981]/40 bg-[#10B981]/10 px-2.5 py-2"
        >
          <Handshake className="h-4 w-4 shrink-0 text-[#10B981]" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-data font-bold uppercase tracking-wide text-[#10B981]">
                {consensus.strong ? "Strong Consensus · AI + Market agree" : "Consensus · AI + Market agree"}
              </span>
            </div>
            <p className="text-[11px] text-slate-300 leading-tight">
              Both back <b className="text-white">{consensus.side}</b> — market{" "}
              <b className="text-white">{consensus.marketPct}%</b>
              {prop.has_real_log ? <> · hit <b className="text-white">{prop.hit_rate}%</b></> : null}
            </p>
          </div>
        </div>
      )}

      {prop.has_real_log && prop.line != null && (
        <div
          data-testid={`ai-projection-${prop.id}`}
          className="mt-3 rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-2.5 py-2"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="flex items-center gap-1 text-[10px] font-data font-bold uppercase tracking-wide text-[#00E5FF]">
              <Sparkles className="h-3 w-3" /> AI Pick · Projection
            </span>
            <span className="text-[10px] font-data text-slate-500">last {prop.last10?.length || 0} games</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="font-data">
              <b className="text-white text-xl tabular-nums" data-testid={`projection-value-${prop.id}`}>{prop.projection}</b>
              <span className="text-slate-400 text-xs"> projected {prop.metric}</span>
            </span>
            <span className={`flex items-center gap-0.5 text-xs font-bold font-data ${over ? "text-[#10B981]" : "text-[#EF4444]"}`}>
              {over ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {prop.lean} {prop.line}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Season avg <b className="text-slate-200">{prop.season_avg}</b> · cleared in{" "}
            <b className="text-slate-200">{prop.hit_rate}%</b> of last {prop.last10?.length || 0}
          </p>
        </div>
      )}

      <PropLeanBar overOdds={prop.over_odds} underOdds={prop.under_odds} />

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          data-testid={`prop-over-button-${prop.id}`}
          onClick={() => addLeg(prop, "OVER")}
          className="flex items-center justify-between rounded-lg border border-[#233248] bg-[#0B0F17] px-2.5 py-1.5 hover:border-[#10B981] transition-colors"
        >
          <span className="text-xs font-semibold text-white">O {prop.line}</span>
          <span className="font-data text-[11px] text-slate-400">{fmtOdds(prop.over_odds)}</span>
        </button>
        <button
          data-testid={`prop-under-button-${prop.id}`}
          onClick={() => addLeg(prop, "UNDER")}
          className="flex items-center justify-between rounded-lg border border-[#233248] bg-[#0B0F17] px-2.5 py-1.5 hover:border-[#EF4444] transition-colors"
        >
          <span className="text-xs font-semibold text-white">U {prop.line}</span>
          <span className="font-data text-[11px] text-slate-400">{fmtOdds(prop.under_odds)}</span>
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <button
          data-testid={`compare-prop-${prop.id}`}
          onClick={() => onCompare(prop)}
          className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
            compareActive
              ? "bg-[#00E5FF] text-[#0B0F17]"
              : "border border-[#233248] text-slate-300 hover:border-[#00E5FF]/50"
          }`}
        >
          {compareActive ? <Check className="h-3.5 w-3.5" /> : <GitCompareArrows className="h-3.5 w-3.5" />}
          {compareActive ? "Selected" : "Compare H2H"}
        </button>
        <button
          data-testid={`quick-add-${prop.id}`}
          onClick={() => addLeg(prop, prop.lean)}
          title="Add AI lean to slip"
          className="rounded-lg border border-[#233248] p-1.5 text-[#00E5FF] hover:bg-[#1B273E] transition-colors"
        >
          {hasLeg(prop.id) ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};
